import React, {Fragment, useContext, useEffect, useState} from "react";
import axiosConfig from "../../../../../Config/axios";
import {getApplicationRoute} from "../../../../../Route/RouteIndex";
import DataTable, {TableColumn} from "react-data-table-component";
import Loader from "../../../../../Layout/Loader";
import IconBtn from "../../../../../CommonElements/IconButton";
import AuthorizationContext from "../../../../../_helper/Authorization";
import {Participant, ParticipantTableOptions, ParticipantTableRef} from "../../Types/ParticipantTypes";
import type {AuthorizationContextType} from "../../../../../_helper/Authorization/AuthorizationTypes";
import WalletContext from "../../../Wallet/Context";
import {WalletContextType} from "../../../Wallet/Context/WalletContext.types";
import {Award, Layout} from "react-feather";

interface participantsTableProps {
    params: Record<string, unknown>;
    tableOptions?: ParticipantTableOptions;
}

const ParticipantsTable = React.forwardRef<ParticipantTableRef, participantsTableProps>(({
                                                params,
                                                tableOptions = {delete: true, view: true}
                                            }, ref) => {
    const [loaderVisibility, setLoaderVisibility] = useState(false);
    const [participants, setParticipants] = useState<Participant[]>([])
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [totalRows, setTotalRows] = useState<number>(0);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const walletContext = useContext(WalletContext) as WalletContextType;
    const {errorHandler} = authContext || {};
    const {getFormattedAmount} = walletContext || {};

    React.useImperativeHandle(ref, () => ({
        getParticipants,
        addParticipant
    }));

    const getParticipants = (page: number = currentPage, limit: number = perPage) => {
        setLoaderVisibility(true);
        const config = {params: {...params, page, per_page: limit}};
        axiosConfig
            .get(getApplicationRoute('participant'), config)
            .then((response: any) => {
                if (response?.data?.data?.data) {
                    setParticipants(response.data.data.data);
                }
                if (response?.data?.data?.meta) {
                    setCurrentPage(response.data.data.meta.current_page);
                    setPerPage(response.data.data.meta.per_page);
                    setTotalRows(response.data.data.meta.total);
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    useEffect(() => {
        getParticipants(1, perPage);
    }, [params])

    const deleteParticipant = (id: number) => {
        setLoaderVisibility(true);
        axiosConfig
            .delete(`${getApplicationRoute('participant')}/${id}`, {})
            .then((response: { data: { data: { id: number; }; }; }) => {
                if (response?.data?.data) {
                    setParticipants(current => current.filter(participantItem => {
                        return participantItem.id !== response?.data?.data?.id;
                    }));
                }
                setLoaderVisibility(false);
            })
            .catch((exception: any) => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    }

    const addParticipant = (participant: Participant) => {
        const participantList = [...participants];
        const existingParticipantIndex = participantList.findIndex(existingParticipant => {
            return existingParticipant.id === participant.id;
        });
        if (existingParticipantIndex !== -1) {
            participantList[existingParticipantIndex] = participant;
        } else {
            participantList.push(participant);
        }
        setParticipants(participantList);
    }

    const participantTableColumns: TableColumn<Participant>[] = [
        {
            name: 'Name',
            selector: (participant: Participant) => participant?.user?.name || participant?.name || '',
            sortable: false,
        },
        {
            name: 'Collector',
            selector: (participant: Participant) => participant?.collector?.name || '',
            sortable: false,
        },
        {
            name: 'Mobile',
            selector: (participant: Participant) => participant?.mobile || participant?.contact_number || '',
            sortable: false,
            right: true,
        },
        {
            name: 'Statistics',
            cell: (row: Participant) => {
                const boardsCount = row.statistics?.boards?.length ?? 0;
                const winningCount = row.statistics?.winningNumbers ?? row.statistics?.winning_numbers ?? 0;

                return <div className='d-flex align-items-center gap-2'>
                    <div className='d-flex align-items-center gap-1' title='Total Boards'>
                        <Layout size={16} style={{cursor: 'help'}}/>
                        <span>{boardsCount}</span>
                    </div>
                    <div className='d-flex align-items-center gap-1' title='Winning Numbers'>
                        <Award size={16} style={{cursor: 'help'}}/>
                        <span>{winningCount}</span>
                    </div>
                </div>
            },
            sortable: false,
        },
        {
            name: 'Wallets',
            cell: (row: Participant) => {
                const cash = row.statistics?.cash ?? row.cashWallet?.current_balance ?? 0;
                const points = row.statistics?.points ?? row.pointWallet?.current_balance ?? 0;
                return (
                    <div className='d-flex align-items-center gap-2 flex-wrap'>
                        <div className='d-flex align-items-center gap-1' title='Cash Wallet Balance'>
                            <span className={`badge badge-light-success m-r-20`}>
                                Cash
                            </span>
                            <span>{getFormattedAmount(cash, 'wallet-type-cash', 1)}</span>
                        </div>
                        <div className='d-flex align-items-center gap-1' title='Points Wallet Balance'>
                            <span className={`badge badge-light-primary m-r-20`}>
                                Points
                            </span>
                            <span>{getFormattedAmount(points, 'wallet-type-points', 1)}</span>
                        </div>
                    </div>
                );
            },
            sortable: false,
        },
        {
            name: 'Option',
            cell: (participant: Participant) => (
                <div className='d-flex align-items-right gap-2'>
                    {tableOptions?.view &&
                        <IconBtn link={`${getApplicationRoute('participant')}/${participant.id}`} type={'view'} permission={'read-participant'}/>}
                    {tableOptions?.delete &&
                        <IconBtn confirm={true} onclick={() => deleteParticipant(participant.id)} type={'delete'} permission={'delete-participant'}/>}
                </div>
            ),
            sortable: false,
            right: true,
        }
    ];
    return <Fragment>
        <Loader visibility={loaderVisibility}/>
        <DataTable pagination paginationServer columns={participantTableColumns}
                   data={participants}
                   paginationTotalRows={totalRows}
                   paginationPerPage={perPage}
                   paginationDefaultPage={currentPage}
                   onChangePage={(page) => getParticipants(page, perPage)}
                   onChangeRowsPerPage={(newPerPage, page) => {
                       setPerPage(newPerPage);
                       getParticipants(page, newPerPage);
                   }}
                   highlightOnHover={true}
                   striped={true} responsive={true}/>
    </Fragment>
});
export default ParticipantsTable;
